import React, { useState, useEffect } from 'react';
import { Modal, Button, Avatar, Popover, Dropdown, Menu, Icon, Tag, Tooltip } from 'antd';
import moment from 'moment';
// Removed UnifiedCalendarDetailsModal.scss as it's now imported via index.scss in SpaceManagementLayout 
import { MANUAL_BOOKING } from '../../Common/constants';

const UnifiedCalendarDetailsModal = ({ 
    visible, 
    onCancel, 
    date, 
    bookings = [], 
    visits = [],
    onEditBooking,
    onCancelBooking,
    onConfirmBooking,
    onReleaseBooking,
    onAcceptVisit,
    onDeclineVisit,
    isAdmin,
    cancellationPolicy
}) => {
    const [activeTab, setActiveTab] = useState('ALL');
    const [acceptVisitPopupVisibility, setAcceptVisitPopupVisibility] = useState(false);
    const [declineVisitPopupVisibility, setDeclineVisitPopupVisibility] = useState(false);
    const [cancelBookingPopupVisibility, setCancelBookingPopupVisibility] = useState(false);
    const [releaseBookingPopupVisibility, setReleaseBookingPopupVisibility] = useState(false);
    const [confirmBookingPopupVisibility, setConfirmBookingPopupVisibility] = useState(false);
    const [currentVisit, setCurrentVisit] = useState(null);
    const [currentBooking, setCurrentBooking] = useState(null);

    // Reset tab when modal opens/closes or date changes
    useEffect(() => {
        if (visible) {
            setActiveTab('ALL');
        } else {
            // Clean up confirmation modals when main modal closes
            setAcceptVisitPopupVisibility(false);
            setDeclineVisitPopupVisibility(false);
            setCancelBookingPopupVisibility(false);
            setReleaseBookingPopupVisibility(false);
            setConfirmBookingPopupVisibility(false);
            setCurrentVisit(null);
            setCurrentBooking(null);
        }
    }, [visible, date]);

    const handleAcceptVisitClick = (visit) => {
        setCurrentVisit(visit);
        setAcceptVisitPopupVisibility(true);
    };

    const handleAcceptVisitYes = () => {
        onAcceptVisit(currentVisit);
        setAcceptVisitPopupVisibility(false);
        // Close main modal after a short delay to allow API to start
        setTimeout(() => onCancel(), 100);
    };

    const handleAcceptVisitNo = () => {
        setAcceptVisitPopupVisibility(false);
    };

    const handleDeclineVisitClick = (visit) => {
        setCurrentVisit(visit);
        setDeclineVisitPopupVisibility(true);
    };

    const handleDeclineVisitYes = () => {
        onDeclineVisit(currentVisit);
        setDeclineVisitPopupVisibility(false);
        // Close main modal after a short delay to allow API to start
        setTimeout(() => onCancel(), 100);
    };

    const handleDeclineVisitNo = () => {
        setDeclineVisitPopupVisibility(false);
    };

    const handleCancelBookingClick = (booking) => {
        setCurrentBooking(booking);
        setCancelBookingPopupVisibility(true);
    };

    const handleCancelBookingYes = () => {
        onCancelBooking(currentBooking);
        setCancelBookingPopupVisibility(false);
        // Close main modal after a short delay to allow API to start
        setTimeout(() => onCancel(), 100);
    };

    const handleCancelBookingNo = () => {
        setCancelBookingPopupVisibility(false);
    };

    const handleReleaseBookingClick = (booking) => {
        setCurrentBooking(booking);
        setReleaseBookingPopupVisibility(true);
    };

    const handleReleaseBookingYes = () => {
        onReleaseBooking(currentBooking);
        setReleaseBookingPopupVisibility(false);
        setTimeout(() => onCancel(), 100);
    };

    const handleReleaseBookingNo = () => {
        setReleaseBookingPopupVisibility(false);
    };

    const handleConfirmBookingClick = (booking) => {
        setCurrentBooking(booking);
        setConfirmBookingPopupVisibility(true);
    };

    const handleConfirmBookingYes = () => {
        onConfirmBooking(currentBooking);
        setConfirmBookingPopupVisibility(false);
        setTimeout(() => onCancel(), 100);
    };

    const handleConfirmBookingNo = () => {
        setConfirmBookingPopupVisibility(false);
    };

    const getTabContent = () => {
        let content = [];
        if (activeTab === 'ALL') {
            content = [...bookings.map(b => ({...b, type: 'booking'})), ...visits.map(v => ({...v, type: 'visit'}))];
        } else if (activeTab === 'BOOKINGS') {
            content = bookings.map(b => ({...b, type: 'booking'}));
        } else if (activeTab === 'VISITS') {
            content = visits.map(v => ({...v, type: 'visit'}));
        }
        return content;
    };

    const items = getTabContent();

    const getBookingStatusColor = (status) => {
        const colors = {
            INITIATED: 'blue',
            TENTATIVE: 'orange',
            CONFIRMED: 'green',
            COMPLETED: 'blue',
            CANCELLED: 'red',
            PENDING: 'orange',
            DECLINED: 'red',
            TERMINATED: 'default',
            WITHDRAWN: 'default'
        };
        return colors[status?.toUpperCase()] || 'default';
    };

    const renderBookingItem = (booking, index) => {
        const isManual = booking?.platformIdentity === MANUAL_BOOKING;
        const isSystemBooking = booking?.platformIdentity === 'SYSTEM_BOOKING';

        const handleEditClick = () => {
            onEditBooking(booking);
            setTimeout(() => onCancel(), 100);
        };

        const handleCancelClick = () => {
            handleCancelBookingClick(booking);
        };

        const handleConfirmClick = () => {
            handleConfirmBookingClick(booking);
        };

        const handleReleaseClick = () => {
            handleReleaseBookingClick(booking);
        };

        const isTentative = booking?.status?.toUpperCase() === 'TENTATIVE';
        const isConfirmed = booking?.status?.toUpperCase() === 'CONFIRMED';
        const showCancelOption = !isSystemBooking || (isSystemBooking && isConfirmed);

        const bookingMenu = (
            <Menu className="booking-action-menu">
                {!isSystemBooking && (
                    <Menu.Item key="edit" onClick={handleEditClick}>
                        <Icon type="edit" /> Edit Reservation
                    </Menu.Item>
                )}
                {isSystemBooking && isTentative && isAdmin && (
                    <Menu.Item key="confirm" onClick={handleConfirmClick}>
                        <Icon type="check" /> Confirm Booking
                    </Menu.Item>
                )}
                {isSystemBooking && isTentative && isAdmin && (
                    <Menu.Item key="release" danger onClick={handleReleaseClick}>
                        <Icon type="close" /> Release Booking
                    </Menu.Item>
                )}
                {showCancelOption && (
                    <Menu.Item key="cancel" danger onClick={handleCancelClick}>
                        <Icon type="delete" /> {isSystemBooking ? 'Cancel Booking' : 'Cancel Reservation'}
                    </Menu.Item>
                )}
            </Menu>
        );

        return (
            <div className="details-card booking-card" key={`booking-${index}`}>
                <div className="card-header">
                     <div className="user-info">
                        <Avatar src={booking.image} size="large" icon="user" shape="square" />
                        <div className="text-info">
                             <div className="badge-container">
                                <span className="badge booking">BOOKINGS</span>
                                {!isManual && booking?.status && (
                                    <Tag color={getBookingStatusColor(booking.status)} className="status-tag-mini">
                                        {booking.status}
                                    </Tag>
                                )}
                             </div>
                             <span className="name">{booking.guest || booking.title}</span>
                             <div className="datetime">
                                {booking.bookingEndDate && booking.bookingEndDate !== booking.bookingStartDate ? (
                                    <>
                                        <div className="current-date-row">
                                            <span>Date: {moment(date).format('DD/MM/YYYY')}</span>
                                        </div>
                                        <div className="full-range-row">
                                            Range: {moment(booking.bookingStartDate, 'DD-MM-YYYY').format('DD/MM/YYYY')} - {moment(booking.bookingEndDate, 'DD-MM-YYYY').format('DD/MM/YYYY')}
                                        </div>
                                    </>
                                ) : (
                                    <span>Date: {moment(booking.bookingStartDate, 'DD-MM-YYYY').format('DD/MM/YYYY')}</span>
                                )}
                                {booking.blocks && booking.blocks[0] && (
                                     <span>Time: {(booking.allDay || !booking.blocks[0].startTime || (booking.blocks[0].startTime && booking.blocks[0].startTime.toLowerCase() === '24/7 access')) 
                                        ? 'All Day' 
                                        : `${booking.blocks[0].startTime} - ${booking.blocks[0].endTime}`}
                                     </span>
                                )}
                             </div>
                        </div>
                     </div>
                     <div className="actions">
                         <div className="platform-info">
                            <span className="platform text-muted">{booking?.platform}</span>
                         </div>
                         {(isManual || (isSystemBooking && isAdmin && (isTentative || isConfirmed))) && (
                             <Dropdown overlay={bookingMenu} trigger={['click']} placement="bottomRight">
                                <Button type="text" size="small">...</Button>
                             </Dropdown>
                         )}
                     </div>
                </div>
            </div>
        );
    }

    const renderVisitItem = (visit, index) => {
        const isPending = visit.schedulerStatus === 'PENDING';
        // STATUS constants from somewhere could be used, assuming strings for now

        return (
             <div className="details-card visit-card" key={`visit-${index}`}>
                <div className="card-header">
                     <div className="user-info">
                        <Avatar src={visit.image} size="large" icon="user" shape="square" /> {/* Visit might not have image, use default */}
                        <div className="text-info">
                             <div className="badge-container">
                                <span className="badge visit">VISITS</span>
                             </div>
                             <span className="name">{visit.firstName} {visit.lastName}</span>
                             <div className="datetime">
                                <span>Date: {visit.schedulerStartDate}</span>
                                <span>Time: {(!visit.schedulerStartTime || visit.schedulerStartTime.toLowerCase() === '24/7 access') ? 'All Day' : visit.schedulerStartTime}</span>
                             </div>
                        </div>
                     </div>
                     <div className="actions">
                        {isPending ? (
                            !isAdmin && (
                            <div className="btn-group">
                                <Button className="btn-decline" onClick={() => handleDeclineVisitClick(visit)}>Decline</Button>
                                <Button className="btn-accept" onClick={() => handleAcceptVisitClick(visit)}>Accept</Button>
                            </div>
                            )
                        ) : (
                            <div className="status-indicator">
                                {visit.schedulerStatus === 'ACTIVE' ? (
                                    <span className="accepted"><span className="check-icon">✔</span> Accepted</span>
                                ) : (
                                    <span className="declined">Declined</span>
                                )}
                            </div>
                        )}
                     </div>
                </div>
            </div>
        );
    }

    const cancellationPolicyLink = cancellationPolicy && cancellationPolicy.name ? (
        <Tooltip
            overlayClassName="cancellation-tooltip"
            title={
                <>
                    <span className="title">{cancellationPolicy.name}</span>
                    <div className="text">{cancellationPolicy.description}</div>
                </>
            }
            placement="bottom"
        >
            <a className="link">cancellation policy</a>
        </Tooltip>
    ) : (
        'cancellation policy'
    );

    return (
        <Modal
            visible={visible}
            onCancel={onCancel}
            footer={null}
            title={date ? moment(date).format('DD MMMM YYYY') : 'Details'}
            className="unified-details-modal"
            centered
            width={600}
        >
            <div className="tabs-header">
                <Button 
                    className={`tab-btn ${activeTab === 'ALL' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('ALL')}
                >
                    All
                </Button>
                <Button 
                    className={`tab-btn ${activeTab === 'BOOKINGS' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('BOOKINGS')}
                >
                    Bookings
                </Button>
                <Button 
                    className={`tab-btn ${activeTab === 'VISITS' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('VISITS')}
                >
                    Visits
                </Button>
            </div>
            
            <div className="items-list">
                {items.length > 0 ? (
                    items.map((item, index) => {
                        if (item.type === 'booking') return renderBookingItem(item, index);
                        return renderVisitItem(item, index);
                    })
                ) : (
                    <div className="no-data">No bookings or visits for this date.</div>
                )}
            </div>

            <Modal
                className="confirmation-modal accept-visit-modal"
                visible={acceptVisitPopupVisibility}
                onCancel={handleAcceptVisitNo}
                title="Accept Visit?"
                centered
                footer={[
                    <Button key="cancel" onClick={handleAcceptVisitNo} className="btn-cancel-modal">
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleAcceptVisitYes} className="btn-accept-modal">
                        Accept
                    </Button>,
                ]}
            >
                <p>Are you sure you want to accept this visit?</p>
            </Modal>

            <Modal
                className="confirmation-modal decline-visit-modal"
                visible={declineVisitPopupVisibility}
                onCancel={handleDeclineVisitNo}
                title="Decline Visit"
                centered
                footer={[
                    <Button key="cancel" onClick={handleDeclineVisitNo} className="btn-cancel-modal">
                        Cancel
                    </Button>,
                    <Button key="submit" onClick={handleDeclineVisitYes} className="btn-decline-modal">
                        Decline
                    </Button>,
                ]}
            >
                <p>Are you sure you want to decline this visit?</p>
            </Modal>

            <Modal
                className="confirmation-modal cancel-reservation-modal"
                visible={cancelBookingPopupVisibility}
                onCancel={handleCancelBookingNo}
                title={currentBooking?.platformIdentity === 'SYSTEM_BOOKING' ? "Cancel Booking" : "Cancel Reservation"}
                centered
                footer={[
                    <Button key="cancel" onClick={handleCancelBookingNo} className="btn-cancel-modal">
                        Cancel
                    </Button>,
                    <Button key="submit" onClick={handleCancelBookingYes} className="btn-yes-modal">
                        Yes
                    </Button>,
                ]}
            >
                {currentBooking?.platformIdentity === 'SYSTEM_BOOKING' ? (
                    <div>
                        <p className="cancel-booking-prompt">Are you sure you want to cancel this booking?</p>
                        <p className="refund-notice-text">A full refund will be automatically initiated for this booking.</p>
                        <p className="ignore-policy-warning">This cancellation ignores the {cancellationPolicyLink}.</p>
                    </div>
                ) : (
                    <p>Are you sure you want to cancel the reservation?</p>
                )}
            </Modal>

            <Modal
                className="confirmation-modal release-booking-modal"
                visible={releaseBookingPopupVisibility}
                onCancel={handleReleaseBookingNo}
                title="Release Booking?"
                centered
                footer={[
                    <Button key="cancel" onClick={handleReleaseBookingNo} className="btn-cancel-modal">
                        No, Keep Booking
                    </Button>,
                    <Button key="submit" type="danger" onClick={handleReleaseBookingYes} className="btn-release-modal">
                        Yes, Release Booking
                    </Button>,
                ]}
            >
                <p>Are you sure you want to release this tentative booking? This will make the slot available again.</p>
            </Modal>

            <Modal
                className="confirmation-modal confirm-booking-modal"
                visible={confirmBookingPopupVisibility}
                onCancel={handleConfirmBookingNo}
                title="Confirm Booking?"
                centered
                footer={[
                    <Button key="cancel" onClick={handleConfirmBookingNo} className="btn-cancel-modal">
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleConfirmBookingYes} className="btn-confirm-modal">
                        Confirm
                    </Button>,
                ]}
            >
                <p>Are you sure you want to confirm this booking?</p>
            </Modal>

        </Modal>
    );
};

export default UnifiedCalendarDetailsModal;
